import { addCallback } from '../../../lib/teact/teactn';

import type {
  ApiMediaExtendedPreview, ApiMessage, ApiReactions,
  MediaContent,
} from '../../../api/types';
import type { ActiveEmojiInteraction, ThreadId } from '../../../types';
import type { RequiredGlobalActions } from '../../index';
import type {
  ActionReturnType, GlobalState, RequiredGlobalState,
} from '../../types';
import { MAIN_THREAD_ID } from '../../../api/types';

import { SERVICE_NOTIFICATIONS_USER_ID } from '../../../config';
import { areDeepEqual } from '../../../util/areDeepEqual';
import { isUserId } from '../../../util/entities/ids';
import { getCurrentTabId } from '../../../util/establishMultitabRole';
import {
  buildCollectionByKey, omit, unique,
} from '../../../util/iteratees';
import { getMessageKey, isLocalMessageId } from '../../../util/keys/messageKey';
import { isInMemory, onBlobUrlRevoked } from '../../../util/mediaLoader';
import { closeRemovedMessageNotifications, notifyAboutMessage } from '../../../util/notifications';
import { onTickEnd } from '../../../util/schedulers';
import { getServerTime } from '../../../util/serverTime';
import { LOCAL_MEDIA_UPLOADING_TEMP_ID } from '../../../api/builders/localMessage';
import {
  addPaidReaction,
  checkIfHasUnreadReactions,
  checkIfReactionEffectIsStale,
  checkIfReactionEffectPlayed,
  checkIfReactionIsUnreadByOther,
  createApiMessageFromTypingDraft,
  getIsSavedDialog,
  getMessageContent,
  getMessageText,
  getSharedMediaTypesForMessage,
  groupMessageIdsByThreadId,
  isActionMessage,
  isDeletedUser,
  isLatestReactionRequest,
  isMessageLocal,
  isTrainingRecordingMessage,
  isUserBot,
  markReactionEffectPlayed,
  pickMatchingTypingDraftMessage,
} from '../../helpers';
import { getMessageReplyInfo, getStoryReplyInfo } from '../../helpers/replies';
import {
  addActionHandler,
  getGlobal,
  setGlobal,
} from '../../index';
import {
  addMessages,
  addViewportId,
  clearMessageSummary,
  clearMessageTranslation,
  deleteChatMessages,
  deleteChatScheduledMessages,
  deletePeerPhoto,
  deleteQuickReply,
  deleteQuickReplyMessages,
  deleteTopic,
  dropLocalMediaPreview,
  isMessageHusk,
  LOCAL_PREVIEW_MEDIA_KINDS,
  removeChatFromChatLists,
  replaceWebPage,
  stampDiscussionRoots,
  updateChat,
  updateChatLastMessageId,
  updateChatMediaLoadingState,
  updateChatMessage,
  updateListedIds,
  updateMessageTranslations,
  updatePeerFullInfo,
  updatePoll,
  updatePollVote,
  updateQuickReplies,
  updateQuickReplyMessage,
  updateScheduledMessage,
  updateSharedMediaCounters,
  updateSharedMediaSearchResults,
} from '../../reducers';
import { addUnreadPollVotes } from '../../reducers/polls';
import { addUnreadReactions, removeUnreadReactions } from '../../reducers/reactions';
import { updateTabState } from '../../reducers/tabs';
import {
  replaceThreadLocalStateParam,
  replaceThreadReadStateParam,
  updateThreadInfo,
  updateThreadInfoLastMessageId,
  updateThreadInfoMessagesCount,
  updateThreadReadState,
} from '../../reducers/threads';
import { updateUserFullInfo } from '../../reducers/users';
import {
  selectCanAnimateSnapEffect,
  selectChat,
  selectChatByClientOrRawId,
  selectChatLastMessageId,
  selectChatMessage,
  selectChatMessages,
  selectChatScheduledMessages,
  selectCommonBoxChatId,
  selectCurrentMessageList,
  selectCurrentSharedMediaSearch,
  selectFirstUnreadId,
  selectIsChatListed,
  selectIsChatWithSelf,
  selectIsMessageInCurrentMessageList,
  selectIsServiceChatReady,
  selectIsViewportNewest,
  selectListedIds,
  selectPerformanceSettingsValue,
  selectPinnedIds,
  selectPollFromMessage,
  selectScheduledIds,
  selectScheduledMessage,
  selectTabState,
  selectTopic,
  selectTopicFromMessage,
  selectUser,
  selectViewportIds,
} from '../../selectors';
import {
  selectDraft,
  selectSavedDialogIdFromMessage,
  selectThread,
  selectThreadByMessage,
  selectThreadIdFromMessage,
  selectThreadInfo,
  selectThreadLocalStateParam,
  selectThreadReadState,
} from '../../selectors/threads';

const ANIMATION_DELAY = 350;
const SNAP_ANIMATION_DELAY = 1000;
const VIDEO_PROCESSING_NOTIFICATION_DELAY = 1000;
// Max age (seconds) for an optimistic message to count as an in-flight twin of an incoming echo.
// Reconciliation races (e.g. multiple open tabs) can strand local messages in the cache; without
// the cutoff such a stale twin would swallow the echo of a later identical send
const SEND_MATCH_MAX_AGE = 60;
const LOCAL_PREVIEW_RELEASE_DELAY = 1000;
const LOCAL_PREVIEW_REVOKE_DELAY = 10000;
const BLOB_URL_REGEX = /blob:[^"]+/g;
let lastVideoProcessingNotificationTime = 0;

type TypingDraftEntry = {
  randomId: string;
  message: ApiMessage;
};

function getTypingDraftEntries<T extends GlobalState>(
  global: T,
  chatId: string,
  threadId: ThreadId,
) {
  const typingDraftStore = selectThreadLocalStateParam(global, chatId, threadId, 'typingDraftIdByRandomId');
  const typingDraftEntries = Object.entries(typingDraftStore || {}).reduce((result, [randomId, messageId]) => {
    const message = selectChatMessage(global, chatId, messageId);
    if (!message?.isTypingDraft) {
      return result;
    }

    result.push({ randomId, message });
    return result;
  }, [] as TypingDraftEntry[]);

  return typingDraftEntries;
}

function removeTypingDraftEntries<T extends GlobalState>(
  global: T,
  chatId: string,
  threadId: ThreadId,
  typingDraftEntries: TypingDraftEntry[],
) {
  if (!typingDraftEntries.length) {
    return global;
  }

  const typingDraftStore = selectThreadLocalStateParam(global, chatId, threadId, 'typingDraftIdByRandomId') || {};
  const randomIds = typingDraftEntries.map(({ randomId }) => randomId);
  const nextTypingDraftStore = omit(typingDraftStore, randomIds);
  const messageIdsToDelete = randomIds.reduce((result, randomId) => {
    const messageId = typingDraftStore[randomId];
    const message = messageId ? selectChatMessage(global, chatId, messageId) : undefined;
    if (!message?.isTypingDraft) {
      return result;
    }

    result.push(messageId);
    return result;
  }, [] as number[]);

  global = replaceThreadLocalStateParam(
    global,
    chatId,
    threadId,
    'typingDraftIdByRandomId',
    Object.keys(nextTypingDraftStore).length ? nextTypingDraftStore : undefined,
  );

  if (messageIdsToDelete.length) {
    global = deleteChatMessages(global, chatId, messageIdsToDelete);
  }

  return global;
}

function shouldBumpGuestBotTopPeer<T extends GlobalState>(global: T, message: ApiMessage) {
  const { guestChatViaId, senderId } = message;
  if (message.isOutgoing || message.content.action || guestChatViaId !== global.currentUserId || !senderId) {
    return false;
  }

  const sender = selectUser(global, senderId);
  return Boolean(sender?.isGuestChatBot);
}

function shouldBumpInlineBotTopPeer(message: ApiMessage) {
  return Boolean(message.isOutgoing && !message.content.action && message.viaBotId);
}

function shouldBumpCorrespondentTopPeer<T extends GlobalState>(global: T, chatId: string) {
  const user = selectUser(global, chatId);
  return Boolean(user && !user.isSelf && !isUserBot(user) && !isDeletedUser(user));
}

// A content signature stable across a send's optimistic (local) message and its server echo, used to
// pair them up so the redundant echo can be dropped. Returns `undefined` for content we cannot match
// confidently (those are never dropped).
function getSendMatchKey<T extends GlobalState>(global: T, message: ApiMessage): string | undefined {
  const { content } = message;
  if (!content) return undefined;
  const media = content.sticker || content.document || content.video || content.audio || content.voice;
  if (media) {
    const { fileName, size, id } = media as { fileName?: string; size?: number; id?: string };
    // Uploaded media (document/photo/video/audio) keeps its filename+size across the optimistic
    // message and the server echo; stickers and saved GIFs are sent by an existing media id, so match
    // on that when there is no filename/size.
    if (fileName || size) return `f:${fileName ?? ''}:${size ?? ''}`;
    if (id) return `i:${id}`;
  }
  // A pasted/uploaded image is sent as `content.photo`, which carries no stable filename/size/id
  // across the optimistic message (`id: 'temp'`, empty `sizes`) and the server echo (real id,
  // populated `sizes`). Pair them by media kind plus the caption — enough to drop the redundant
  // echo, since the send always reconciles the correct message in place via its own response.
  if (content.photo) return `p:${content.text?.text ?? ''}`;
  // A poll's optimistic message and its server echo share the question text, but the poll id is
  // server-assigned and differs across them, and the message itself only carries `pollId` — the
  // poll object lives in `pollById` (with an untyped copy inlined on REST messages). Pair them by
  // the question text so the redundant echo is dropped instead of painting a second bubble that
  // jumps then vanishes.
  if (content.pollId) {
    const poll = selectPollFromMessage(global, message);
    return poll ? `poll:${poll.summary.question.text}` : undefined;
  }
  // A dice carries no text/media to pair on, and its `value` differs between the optimistic message
  // (rolled locally as `-1`) and the server echo — so key on the stable `emoticon`. Without this the
  // echo isn't recognized as the pending send's twin, so it paints a second bubble and the message
  // reconciles out-of-place (losing `previousLocalId`) instead of in place.
  if (content.dice) return `d:${content.dice.emoticon}`;
  const text = content.text?.text;
  if (text) return `t:${text}`;
  return undefined;
}

// Whether `serverMessage` is the landed copy of the optimistic `localMessage` — i.e. the send went
// through but its response never reconciled the local one (e.g. the worker died mid-send)
export function getIsLandedSendTwin<T extends GlobalState>(
  global: T, localMessage: ApiMessage, serverMessage: ApiMessage,
) {
  if (!serverMessage.isOutgoing || isMessageLocal(serverMessage)) return false;
  if (Math.abs(serverMessage.date - localMessage.date) >= SEND_MATCH_MAX_AGE) return false;

  const matchKey = getSendMatchKey(global, localMessage);
  return matchKey !== undefined && matchKey === getSendMatchKey(global, serverMessage);
}

export function deleteLandedLocalMessages<T extends GlobalState>(
  global: T, chatId: string, serverMessages: ApiMessage[],
): T {
  const landedLocalIds = Object.values(selectChatMessages(global, chatId) || {})
    .filter((message) => isMessageLocal(message) && message.isOutgoing && serverMessages.some(
      (serverMessage) => getIsLandedSendTwin(global, message, serverMessage),
    ))
    .map(({ id }) => id);

  return landedLocalIds.length ? deleteChatMessages(global, chatId, landedLocalIds) : global;
}

// Previews already handed back (see `releaseClosedChatPreviews`): a copy still in flight must not graft them again
const releasedPreviewUrls = new Set<string>();

// When a media send reconciles, the server message replaces the optimistic one but carries no local
// `blobUrl` — so the Photo/Video element (which prefers `blobUrl` over the CDN `mediaData`) drops the
// already-loaded blob and re-fetches the CDN url, flashing to black then re-loading. Graft the local
// blob back onto the matching server media so it keeps rendering the preview without a reload.
function preserveLocalMediaPreview(
  localContent: MediaContent | undefined,
  serverContent: MediaContent | undefined,
): MediaContent | undefined {
  if (!localContent || !serverContent) return serverContent;

  let result = serverContent;
  const MEDIA_KINDS = ['photo', 'video', 'document', 'audio', 'voice'] as const;
  for (const kind of MEDIA_KINDS) {
    const localBlobUrl = (localContent[kind] as { blobUrl?: string } | undefined)?.blobUrl;
    const serverMedia = result[kind] as { blobUrl?: string } | undefined;
    if (localBlobUrl && serverMedia && !serverMedia.blobUrl && !releasedPreviewUrls.has(localBlobUrl)) {
      result = { ...result, [kind]: { ...serverMedia, blobUrl: localBlobUrl } };
    }
  }

  // An uploaded GIF renders locally as a photo (`image/gif` is a photo type) but comes back as a
  // video (gif -> mp4). The kinds differ, so there is no blob to reuse — graft the local photo blob
  // as the video's poster so it shows during buffering instead of a black frame.
  const gifPreview = localContent.photo?.blobUrl;
  if (gifPreview && result.video && !result.video.blobUrl && !result.video.thumbnail?.dataUri
    && !releasedPreviewUrls.has(gifPreview)) {
    result = {
      ...result,
      video: {
        ...result.video,
        thumbnail: { width: result.video.width ?? 0, height: result.video.height ?? 0, dataUri: gifPreview },
      },
    };
  }

  return result;
}

// The graft above is the only place a blob URL owned by the media cache is copied into a message, and
// the cache can revoke it at any time — LRU eviction, or the shrink that runs while the tab is in the
// background. The message would then be stuck on a dead URL, since media carrying a `blobUrl` is never
// re-fetched. Remember who holds each grafted URL (one entry per media sent this session, dropped as
// soon as it is revoked) so the preview can be cleared and the media loaded normally instead.
const localPreviewOwners = new Map<string, { chatId: string; messageId: number }>();
let prevByTabId: GlobalState['byTabId'] | undefined;
let releaseTimeout: number | undefined;

onBlobUrlRevoked((url) => {
  const owner = localPreviewOwners.get(url);
  if (!owner) return;

  localPreviewOwners.delete(url);
  // A revoke can happen deep inside a media load, so never touch the store in that same tick.
  onTickEnd(() => {
    let global = getGlobal();
    global = dropLocalMediaPreview(global, owner.chatId, owner.messageId, url);
    setGlobal(global);
  });
});

function trackLocalMediaPreview(chatId: string, messageId: number, content: MediaContent) {
  LOCAL_PREVIEW_MEDIA_KINDS.forEach((kind) => {
    const blobUrl = (content[kind] as { blobUrl?: string } | undefined)?.blobUrl;
    if (!blobUrl) return;
    localPreviewOwners.set(blobUrl, { chatId, messageId });
  });
}

// The optimistic message's own previews (a video's poster, a photo's thumbnail) do not carry over to the
// server message, and the upload that read them is done by now. Nothing would revoke them otherwise;
// a poster is a full-resolution PNG. The delay lets the bubble finish switching to the server media.
function revokeDroppedLocalUrls(localContent: MediaContent, serverContent: MediaContent) {
  const keptUrls = JSON.stringify(serverContent);
  const droppedUrls = unique(JSON.stringify(localContent).match(BLOB_URL_REGEX) || [])
    .filter((url) => !keptUrls.includes(url) && !isInMemory(url));
  if (!droppedUrls.length) return;

  setTimeout(() => droppedUrls.forEach((url) => URL.revokeObjectURL(url)), ANIMATION_DELAY);
}

// A URL grafted from an attachment is not one the media cache owns, so nothing above ever revokes it:
// every photo or video sent stayed in memory (a pasted screenshot is not backed by a file on disk) until
// the app quit, with its bubble decoding it at full resolution. Once its chat is closed in every tab the
// preview has served its purpose, so give it back and let the server media take over.
// The delay lets the chat finish sliding out before its bubbles switch source.
addCallback((global: GlobalState) => {
  if (global.byTabId === prevByTabId) return;
  prevByTabId = global.byTabId;

  if (!localPreviewOwners.size || releaseTimeout) return;
  releaseTimeout = window.setTimeout(releaseClosedChatPreviews, LOCAL_PREVIEW_RELEASE_DELAY);
});

function releaseClosedChatPreviews() {
  releaseTimeout = undefined;

  let global = getGlobal();
  const initialGlobal = global;
  const openChatIds = new Set(Object.keys(global.byTabId)
    .map((tabId) => selectCurrentMessageList(initialGlobal, Number(tabId))?.chatId));

  const releasedUrls: string[] = [];
  localPreviewOwners.forEach((owner, url) => {
    if (openChatIds.has(owner.chatId)) return;

    localPreviewOwners.delete(url);
    releasedPreviewUrls.add(url);
    global = dropLocalMediaPreview(global, owner.chatId, owner.messageId, url);
    if (!isInMemory(url)) releasedUrls.push(url);
  });

  if (global !== initialGlobal) setGlobal(global);
  // A forward of the message made just before leaving its chat shows a copy of the preview until the
  // server answers, so give that copy time to be replaced (it is never grafted again, see above)
  if (releasedUrls.length) {
    setTimeout(() => releasedUrls.forEach((url) => URL.revokeObjectURL(url)), LOCAL_PREVIEW_REVOKE_DELAY);
  }
}

addActionHandler('apiUpdate', (global, actions, update): ActionReturnType => {
  switch (update['@type']) {
    case 'newMessage': {
      const {
        id, shouldForceReply, wasDrafted, poll, webPage,
      } = update;
      // The backend's realtime bot-message broadcast stamps a supergroup's `chatId` in the
      // plain-negative group form (`-132`) instead of the client channel form (`-1000000000132`) the
      // same chat uses in history, so a direct lookup misses and the message would land in a phantom
      // chat (visible only after a reload). Resolve the stored chat by raw peer id and re-stamp the
      // message to its client id so realtime rendering targets the open chat. No-op once the backend
      // sends the correct id.
      const chat = selectChatByClientOrRawId(global, update.chatId);
      const chatId = chat?.id ?? update.chatId;
      const message = chat && chat.id !== update.message.chatId
        ? { ...update.message, chatId: chat.id }
        : update.message;
      // A payload the list cannot place never reaches the store (see `omitHusks`), and everything
      // below reads the message back and assumes it is there. Ignore the update rather than half-
      // applying it: an id nothing is known about is a state the list already handles, and the next
      // history fetch fills it in if the server does have a real message for it.
      if (isMessageHusk(message)) return;

      const isLocal = isMessageLocal(message);

      // The realtime frame, unlike history, carries no users, so a forward from someone outside the
      // chat showed "Forwarded from" with no name until a reload. `users/by-ids` refuses a stranger,
      // but the message fetch returns the users it references
      const forwardFromId = message.forwardInfo?.fromId;
      if (!isLocal && forwardFromId && isUserId(forwardFromId) && !selectUser(global, forwardFromId)) {
        actions.loadMessage({ chatId, messageId: id });
      }

      const threadId = selectThreadIdFromMessage(global, message) || MAIN_THREAD_ID;
      const typingDraftEntries = getTypingDraftEntries(global, chatId, threadId);
      const hasTypingDraftsInThread = Boolean(typingDraftEntries.length);
      const shouldAttemptTypingDraftHandoff = !isLocal && !message.isOutgoing && !message.content.action;

      let matchedTypingDraftEntry: TypingDraftEntry | undefined;
      let shouldClearTypingDraftsAfterRender = false;

      if (hasTypingDraftsInThread && shouldAttemptTypingDraftHandoff) {
        const matchedTypingDraft = pickMatchingTypingDraftMessage(
          message,
          typingDraftEntries.map(({ message: typingDraftMessage }) => typingDraftMessage),
        );

        matchedTypingDraftEntry = matchedTypingDraft
          ? typingDraftEntries.find(
            ({ message: typingDraftMessage }) => typingDraftMessage.id === matchedTypingDraft.id,
          )
          : undefined;
        shouldClearTypingDraftsAfterRender = Boolean(typingDraftEntries.length && !matchedTypingDraftEntry);
      }

      // The realtime `newMessage` echo of the user's own just-sent message carries no
      // `previousLocalId`, so it would overwrite the already-reconciled message and flip its render
      // key from the local id to the server id — remounting the bubble (a visible flash / jump). Keep
      // the existing `previousLocalId` so the key stays stable and the send reconciles in place.
      const existingMessage = selectChatMessage(global, chatId, id);

      // Store the poll early so `getSendMatchKey` can resolve both the incoming message's poll and
      // the optimistic twin's one from `pollById` when pairing them up below
      if (poll) {
        global = updatePoll(global, poll.summary.id, poll);
      }

      // The realtime `newMessage` echo of the user's own send can arrive before the send API response
      // reconciles the optimistic message. Adding it then paints a second bubble next to the still
      // pending one until the response merges them — the "two files/messages, one vanishes" flash.
      // Drop the echo while a matching optimistic (local) send is in flight; the send response
      // (`updateMessageSendSucceeded`) reconciles that message in place without a flash.
      if (!isLocal && message.isOutgoing && !existingMessage) {
        const matchKey = getSendMatchKey(global, message);
        const chatMessages = matchKey ? selectChatMessages(global, chatId) : undefined;
        const hasPendingLocalTwin = chatMessages !== undefined && Object.values(chatMessages).some(
          (m) => isMessageLocal(m) && m.isOutgoing && message.date - m.date < SEND_MATCH_MAX_AGE
            && getSendMatchKey(global, m) === matchKey,
        );
        if (hasPendingLocalTwin) return;
      }

      const nextMessage = matchedTypingDraftEntry ? {
        ...message,
        previousLocalId: matchedTypingDraftEntry.message.id,
        isTypingDraft: true,
        wasTypingDraft: true,
      } : (existingMessage?.previousLocalId && !message.previousLocalId ? {
        ...message,
        previousLocalId: existingMessage.previousLocalId,
      } : message);

      global = updateWithLocalMedia(global, chatId, id, true, nextMessage);
      // A realtime message bypasses `addChatMessagesById`, so it misses the ingest-time stamping that
      // turns a discussion group's copy of a channel post into a channel post. Without this a post
      // published while the group is open renders as a plain member bubble (the admin's own outgoing
      // one, at that) until the comments thread is opened. Passing the message also re-stamps the root
      // a just-arrived comment points at. Read the message back from the store rather than trusting
      // `nextMessage`: an id-less husk is dropped on the way in (see `omitHusks`), and stamping one
      // would throw and take the whole update handler down with it.
      const storedMessage = selectChatMessage(global, chatId, id);
      if (storedMessage) {
        global = stampDiscussionRoots(global, chatId, { [id]: storedMessage });
      }
      global = updateListedAndViewportIds(global, nextMessage);

      if (hasTypingDraftsInThread && matchedTypingDraftEntry) {
        global = removeTypingDraftEntries(global, chatId, threadId, [matchedTypingDraftEntry]);
      }

      const newMessage = selectChatMessage(global, chatId, id)!;
      const replyInfo = getMessageReplyInfo(newMessage);
      const storyReplyInfo = getStoryReplyInfo(newMessage);
      if (chat?.isForum
        && replyInfo?.isForumTopic
        && !selectTopicFromMessage(global, newMessage)
        && replyInfo.replyToMsgId) {
        actions.loadTopicById({ chatId, topicId: replyInfo.replyToMsgId });
      }

      Object.values(global.byTabId).forEach(({ id: tabId }) => {
        // Force update for last message on drafted messages to prevent flickering
        if (isLocal && wasDrafted) {
          global = updateChatLastMessage(global, chatId, newMessage);
        }

        const messageThreadId = selectThreadIdFromMessage(global, newMessage);
        global = updateChatMediaLoadingState(global, newMessage, chatId, messageThreadId, tabId);

        if (selectIsMessageInCurrentMessageList(global, chatId, message, tabId)) {
          // Reactively extend an already-open Shared Media tab (Media/Documents/Links/Audio/Voice/
          // Gif) with the new message, so it doesn't require reopening the tab to show up.
          const matchingSharedMediaTypes = getSharedMediaTypesForMessage(newMessage);
          if (matchingSharedMediaTypes.length) {
            const currentSharedMediaSearch = selectCurrentSharedMediaSearch(global, tabId);
            const currentMessageListForSearch = selectCurrentMessageList(global, tabId)!;
            matchingSharedMediaTypes.forEach((type) => {
              // The training-recordings tab only exists while the count is positive, so the first one must show it
              const counters = currentSharedMediaSearch?.counters;
              if (type === 'trainingRecording' && counters) {
                global = updateSharedMediaCounters(global, chatId, currentMessageListForSearch.threadId, {
                  ...counters,
                  trainingRecording: (counters.trainingRecording ?? 0) + 1,
                }, tabId);
              }

              const existingResults = currentSharedMediaSearch?.resultsByType?.[type];
              if (!existingResults) return;

              global = updateSharedMediaSearchResults(
                global, chatId, currentMessageListForSearch.threadId, type, [id],
                existingResults.totalCount !== undefined ? existingResults.totalCount + 1 : undefined,
                existingResults.nextOffsetId,
                tabId,
              );
            });
          }

          if (isLocal && message.isOutgoing && !(message.content?.action) && !storyReplyInfo?.storyId
            && !message.content?.storyData) {
            const currentMessageList = selectCurrentMessageList(global, tabId);
            if (currentMessageList) {
              // We do not use `actions.focusLastMessage` as it may be set with a delay (see below)
              actions.focusMessage({
                chatId,
                threadId: currentMessageList.threadId,
                messageId: message.id,
                noHighlight: true,
                isResizingContainer: true,
                tabId,
              });
            }
          }

          // @perf Wait until scroll animation finishes or simply rely on delivery status update
          // (which is itself delayed)
          if (!isLocal) {
            setTimeout(() => {
              global = getGlobal();
              if (shouldForceReply) {
                actions.updateDraftReplyInfo({
                  replyToMsgId: id,
                  tabId,
                });
              }
              global = updateChatLastMessage(global, chatId, newMessage);
              setGlobal(global);
            }, ANIMATION_DELAY);
          }
        } else {
          global = updateChatLastMessage(global, chatId, newMessage);
        }

        if (!isLocal && message.isOutgoing && message.content?.action?.type === 'noForwardsRequest') {
          const currentMessageList = selectCurrentMessageList(global, tabId);
          if (currentMessageList?.chatId === chatId && currentMessageList.type === 'thread') {
            actions.focusMessage({
              chatId,
              threadId: MAIN_THREAD_ID,
              messageId: message.id,
              noHighlight: true,
              tabId,
            });
          }
        }
      });

      if (webPage) {
        global = replaceWebPage(global, webPage.id, webPage);
      }

      if (message.reportDeliveryUntilDate && message.reportDeliveryUntilDate > getServerTime()) {
        actions.reportMessageDelivery({ chatId, messageId: id });
      }

      if (shouldClearTypingDraftsAfterRender) {
        onTickEnd(() => {
          global = getGlobal();
          global = removeTypingDraftEntries(global, chatId, threadId, typingDraftEntries);
          setGlobal(global);
        });
      }

      if (!isLocal && message.content?.action?.type === 'noForwardsToggle') {
        const { newValue } = message.content.action;
        if (message.isOutgoing) {
          global = updateUserFullInfo(global, chatId, {
            noForwardsMyEnabled: newValue,
          });
          const tabId = getCurrentTabId();
          if (selectCurrentMessageList(global, tabId)?.chatId === chatId) {
            actions.showNotification({
              icon: newValue ? 'hand-stop-filled' : 'select-filled',
              message: { key: newValue ? 'NotificationSharingDisabled' : 'NotificationSharingEnabled' },
              tabId,
            });
          }
        } else {
          const originalMessage = replyInfo?.replyToMsgId ?
            selectChatMessage(global, chatId, replyInfo.replyToMsgId) : undefined;

          // When peer accepted user request to enable sharing
          if (originalMessage?.isOutgoing && !newValue) {
            global = updateUserFullInfo(global, chatId, {
              noForwardsMyEnabled: false,
            });
          } else {
            global = updateUserFullInfo(global, chatId, {
              noForwardsPeerEnabled: newValue,
            });
          }
        }
      }

      setGlobal(global);

      if (shouldBumpGuestBotTopPeer(global, newMessage)) {
        actions.bumpTopPeerRating({
          category: 'botsGuestChat',
          peerId: newMessage.senderId!,
          date: newMessage.date,
        });
      }

      if (shouldBumpInlineBotTopPeer(newMessage)) {
        actions.bumpTopPeerRating({
          category: 'botsInline',
          peerId: newMessage.viaBotId!,
          date: newMessage.date,
        });
      }

      // A message arriving from the server is proof the user is still in the chat. `isNotJoined` gets
      // set by paths that cannot tell our own leave from someone else's, and it hides the chat from
      // every list and folder — so the flag and the message contradict each other, and the message is
      // the one that came from the server about this chat right now.
      //
      // The refresh below used to be skipped for exactly these chats (`!chat?.isNotJoined`), which
      // meant a flagged chat could receive messages all day and still never come back: the only
      // evidence that disproved the flag was also the reason to ignore it. Clear it first, then let
      // the refresh re-list the chat.
      // Re-read: the `bumpTopPeerRating` dispatches above run their handlers synchronously, so the
      // `global` captured before them is already outdated here and writing it back throws.
      if (!isLocal && chat?.isNotJoined) {
        global = getGlobal();
        global = updateChat(global, chatId, { isNotJoined: undefined });
        setGlobal(global);
      }

      // Reload dialogs if chat is not present in the list
      if (!isLocal && !selectIsChatListed(global, chatId)) {
        actions.loadTopChats();
      }

      if (!isLocal && selectIsChatWithSelf(global, chatId)) {
        const savedDialogId = selectSavedDialogIdFromMessage(global, newMessage);
        if (savedDialogId && !selectIsChatListed(global, savedDialogId, 'saved')) {
          actions.requestSavedDialogUpdate({ chatId: savedDialogId });
        }
      }

      break;
    }

    case 'updateChatLastMessage': {
      const { id, lastMessage } = update;

      global = updateChatLastMessage(global, id, lastMessage, true);
      // `lastMessage` here is a preview-only projection (e.g. missing `replyInfo`) — never let it
      // overwrite a fuller message a real fetch already stored; only fill the id if it's missing.
      global = addMessages(global, [lastMessage], false);
      setGlobal(global);
      break;
    }

    case 'updateStartEmojiInteraction': {
      Object.values(global.byTabId).forEach(({ id: tabId }) => {
        const { chatId: currentChatId } = selectCurrentMessageList(global, tabId) || {};

        if (currentChatId !== update.id) return;
        const message = selectChatMessage(global, currentChatId, update.messageId);

        if (!message) return;

        // Workaround for a weird behavior when interaction is received after watching reaction
        if (getMessageText(message)?.text !== update.emoji) return;

        const tabState = selectTabState(global, tabId);
        global = updateTabState(global, {
          activeEmojiInteractions: [...(tabState.activeEmojiInteractions || []), {
            id: Math.random(),
            animatedEffect: update.emoji,
            messageId: update.messageId,
          } as ActiveEmojiInteraction],
        }, tabId);
      });

      setGlobal(global);

      break;
    }

    case 'newScheduledMessage': {
      const {
        chatId, id, message, poll, webPage,
      } = update;

      global = updateWithLocalMedia(global, chatId, id, true, message, true);

      const scheduledIds = selectScheduledIds(global, chatId, MAIN_THREAD_ID) || [];
      global = replaceThreadLocalStateParam(
        global, chatId, MAIN_THREAD_ID, 'scheduledIds', unique([...scheduledIds, id]),
      );

      const threadId = selectThreadIdFromMessage(global, message);
      if (threadId !== MAIN_THREAD_ID) {
        const threadScheduledIds = selectScheduledIds(global, chatId, threadId) || [];
        global = replaceThreadLocalStateParam(
          global, chatId, threadId, 'scheduledIds', unique([...threadScheduledIds, id]),
        );
      }

      if (poll) {
        global = updatePoll(global, poll.summary.id, poll);
      }

      if (webPage) {
        global = replaceWebPage(global, webPage.id, webPage);
      }

      global = updatePeerFullInfo(global, chatId, {
        hasScheduledMessages: true,
      });

      setGlobal(global);

      break;
    }

    case 'updateScheduledMessage': {
      const {
        chatId, id, message, poll, webPage, isFromNew,
      } = update;

      const currentMessage = selectScheduledMessage(global, chatId, id);
      if (!currentMessage) {
        if (isFromNew) {
          actions.apiUpdate({
            '@type': 'newScheduledMessage',
            id: update.id,
            chatId: update.chatId,
            message: update.message as ApiMessage,
            poll: update.poll,
            webPage: update.webPage,
          });
        }
        return;
      }

      global = updateWithLocalMedia(global, chatId, id, false, message, true);
      const ids = Object.keys(selectChatScheduledMessages(global, chatId) || {}).map(Number).sort((a, b) => b - a);
      global = replaceThreadLocalStateParam(global, chatId, MAIN_THREAD_ID, 'scheduledIds', ids);

      const threadId = selectThreadIdFromMessage(global, currentMessage);
      if (threadId !== MAIN_THREAD_ID) {
        const threadScheduledIds = selectScheduledIds(global, chatId, threadId) || [];
        global = replaceThreadLocalStateParam(
          global, chatId, threadId, 'scheduledIds', [...threadScheduledIds].sort((a, b) => b - a),
        );
      }
      if (poll) {
        global = updatePoll(global, poll.summary.id, poll);
      }

      if (webPage) {
        global = replaceWebPage(global, webPage.id, webPage);
      }

      setGlobal(global);

      break;
    }

    case 'updateMessage': {
      const {
        chatId, id, message, poll, webPage, isFromNew, isFull, shouldForceReply,
      } = update;

      const currentMessage = selectChatMessage(global, chatId, id);

      if (message?.reactions) {
        global = updateReactions(
          global, actions, {
            chatId,
            id,
            reactions: message.reactions,
          },
        );
      }

      if (poll) {
        global = updatePoll(global, poll.summary.id, poll);
      }

      if (webPage) {
        global = replaceWebPage(global, webPage.id, webPage);
      }

      if (!currentMessage) {
        if (isFromNew && isFull) {
          actions.apiUpdate({
            '@type': 'newMessage',
            id: update.id,
            chatId: update.chatId,
            message: update.message,
            poll: update.poll,
            webPage: update.webPage,
            shouldForceReply,
          });
        }

        // If update contains the full message, store it
        if (update.isFull) {
          global = addMessages(global, [update.message]);
        }
        setGlobal(global);
        return;
      }

      if (message?.content?.text?.text !== currentMessage?.content?.text?.text) {
        global = clearMessageTranslation(global, chatId, id);
        global = clearMessageSummary(global, chatId, id);
      }

      if (message) {
        global = updateWithLocalMedia(global, chatId, id, false, message);
      }

      setGlobal(global);

      // Not gated on the stored copy being unrecalled: the recaller's own optimistic marker is already
      // there when the server's copy lands, and their composer may be replying to that very message
      if (message?.isRecalled) {
        cancelRepliesToRecalledMessage(global, actions, chatId, id);
        // The banner still shows the text that was taken back
        closeRemovedMessageNotifications(chatId, [id]);
      }

      break;
    }

    case 'updateQuickReplyMessage': {
      const { id, message, poll, webPage } = update;

      global = updateQuickReplyMessage(global, id, message);

      if (poll) {
        global = updatePoll(global, poll.summary.id, poll);
      }

      if (webPage) {
        global = replaceWebPage(global, webPage.id, webPage);
      }

      setGlobal(global);

      break;
    }

    case 'deleteQuickReplyMessages': {
      const { messageIds } = update;

      global = deleteQuickReplyMessages(global, messageIds);
      setGlobal(global);

      break;
    }

    case 'updateQuickReplies': {
      const { quickReplies } = update;
      const byId = buildCollectionByKey(quickReplies, 'id');

      global = updateQuickReplies(global, byId);
      setGlobal(global);
      break;
    }

    case 'deleteQuickReply': {
      global = deleteQuickReply(global, update.quickReplyId);
      setGlobal(global);
      break;
    }

    case 'updateVideoProcessingPending': {
      const {
        chatId, localId, newScheduledMessageId,
      } = update;

      // The scheduled message that replaces this one shows the same media — keep it loaded.
      global = deleteChatMessages(global, chatId, [localId], true);
      global = updatePeerFullInfo(global, chatId, {
        hasScheduledMessages: true,
      });

      setGlobal(global);

      Object.values(global.byTabId).forEach(({ id: tabId }) => {
        const currentMessageList = selectCurrentMessageList(global, tabId);
        if (currentMessageList?.chatId !== chatId) return;

        const now = Date.now();
        if (now - lastVideoProcessingNotificationTime < VIDEO_PROCESSING_NOTIFICATION_DELAY) {
          return;
        }
        lastVideoProcessingNotificationTime = now;

        actions.showNotification({
          message: {
            key: 'VideoConversionText',
          },
          title: {
            key: 'VideoConversionTitle',
          },
          tabId,
        });

        actions.focusMessage({
          chatId,
          messageId: newScheduledMessageId,
          messageListType: 'scheduled',
          tabId,
        });
      });

      break;
    }

    case 'updateMessageSendSucceeded': {
      const {
        chatId, localId, poll,
      } = update;

      const currentMessage = selectChatMessage(global, chatId, localId);

      // Every message the backend returns for a discussion supergroup carries a `replyInfo` that
      // points at the message itself (`replyToMsgId === replyToTopId === id`, `isForumTopic: true`).
      // That is not a reply, but the thread id is derived from it — so a comment reconciled with the
      // server payload was re-threaded from the comments thread into a phantom one of its own, and
      // the Comments view stayed on "No Comments" until a reload refetched the thread. The local
      // message already knows the thread it was composed in, so keep its reply info in that case.
      const serverReplyInfo = getMessageReplyInfo(update.message);
      const isSelfReferencingReply = serverReplyInfo?.type === 'message'
        && (serverReplyInfo.replyToMsgId === update.message.id
          || serverReplyInfo.replyToTopId === update.message.id);
      const message = isSelfReferencingReply && currentMessage?.replyInfo
        ? { ...update.message, replyInfo: currentMessage.replyInfo }
        : update.message;

      global = updateListedAndViewportIds(global, message);

      // The optimistic copy is only making way for the server message below, which reuses its media
      // (see `preserveLocalMediaPreview`) — so its blob must survive the swap.
      global = deleteChatMessages(global, chatId, [localId], true);

      // Edge case for "Send When Online"
      if (message.isScheduled) {
        global = deleteChatScheduledMessages(global, chatId, [localId]);
      }

      global = updateChatMessage(global, chatId, message.id, {
        ...currentMessage,
        ...message,
        content: preserveLocalMediaPreview(currentMessage?.content, message.content) ?? message.content,
        previousLocalId: localId,
        isDeleting: undefined,
        // A pending optimistic message (e.g. media upload) carries `messageSendingStatePending`;
        // the server message omits the field, so clear it explicitly or it stays stuck "sending".
        sendingState: undefined,
      });

      if (poll) {
        global = updatePoll(global, poll.summary.id, poll);
      }

      global = {
        ...global,
        fileUploads: {
          byMessageKey: omit(global.fileUploads.byMessageKey, [getMessageKey(message)]),
        },
      };

      // The store drops a message with nothing to render (see `omitHusks`), so a send response that
      // came back stripped leaves nothing to read back — fall back to the payload instead of
      // asserting it landed, or the sender's own confirmed send takes the handler down with it.
      const newMessage = selectChatMessage(global, chatId, message.id) || message;
      trackLocalMediaPreview(chatId, message.id, newMessage.content);
      if (currentMessage) revokeDroppedLocalUrls(currentMessage.content, newMessage.content);
      global = updateChatLastMessage(global, chatId, newMessage);

      const thread = selectThreadByMessage(global, message);
      // For some reason TheAdvance requires to manually mark outgoing thread messages read
      Object.values(global.byTabId).forEach(({ id: tabId }) => {
        const { chatId: currentChatId, threadId: currentThreadId } = selectCurrentMessageList(global, tabId) || {};
        if (currentChatId !== chatId
          || (thread?.threadInfo?.threadId || MAIN_THREAD_ID) !== currentThreadId) {
          return;
        }

        actions.markMessageListRead({ maxId: message.id, tabId });
      });
      if (thread?.threadInfo?.threadId) {
        global = replaceThreadReadStateParam(
          global, chatId, thread.threadInfo.threadId, 'lastReadInboxMessageId', message.id,
        );
        global = updateThreadInfoLastMessageId(global, chatId, thread.threadInfo.threadId, message.id);
      }

      const chat = selectChat(global, chatId);
      // The server accepted a message we sent to this chat, which cannot be true of a chat we are no
      // longer in — so a lingering `isNotJoined` is wrong, and leaving it set would both hide the chat
      // from every list and folder and (as the gate here used to) suppress the very refresh that
      // re-lists it. Same reasoning as in `updateNewMessage`.
      if (chat?.isNotJoined) {
        global = updateChat(global, chatId, { isNotJoined: undefined });
      }

      // Reload dialogs if chat is not present in the list
      if (!selectIsChatListed(global, chatId)) {
        actions.loadTopChats();
      }

      if (selectIsChatWithSelf(global, chatId)) {
        const savedDialogId = selectSavedDialogIdFromMessage(global, newMessage);
        if (savedDialogId && !selectIsChatListed(global, savedDialogId, 'saved')) {
          actions.requestSavedDialogUpdate({ chatId: savedDialogId });
        }
      }

      setGlobal(global);

      if (shouldBumpCorrespondentTopPeer(global, chatId)) {
        actions.bumpTopPeerRating({ category: 'correspondents', peerId: chatId });
      }

      break;
    }

    case 'updateScheduledMessageSendSucceeded': {
      const {
        chatId, localId, message, poll,
      } = update;
      const scheduledIds = selectScheduledIds(global, chatId, MAIN_THREAD_ID) || [];
      global = replaceThreadLocalStateParam(
        global, chatId, MAIN_THREAD_ID, 'scheduledIds', [...scheduledIds, message.id],
      );

      const threadId = selectThreadIdFromMessage(global, message);
      if (threadId !== MAIN_THREAD_ID) {
        const threadScheduledIds = selectScheduledIds(global, chatId, threadId) || [];
        global = replaceThreadLocalStateParam(
          global, chatId, threadId, 'scheduledIds', [...threadScheduledIds, message.id],
        );
      }

      const currentMessage = selectScheduledMessage(global, chatId, localId);

      global = deleteChatScheduledMessages(global, chatId, [localId]);
      global = updateScheduledMessage(global, chatId, message.id, {
        ...currentMessage,
        ...message,
        previousLocalId: localId,
        isDeleting: undefined,
        // Clear the optimistic pending flag — the server message omits it (see send-succeeded above).
        sendingState: undefined,
      });

      if (poll) {
        global = updatePoll(global, poll.summary.id, poll);
      }

      setGlobal(global);
      if (shouldBumpCorrespondentTopPeer(global, chatId)) {
        actions.bumpTopPeerRating({ category: 'correspondents', peerId: chatId });
      }
      break;
    }

    case 'updatePinnedIds': {
      const { chatId, isPinned, messageIds } = update;
      const shouldBePinned = Boolean(isPinned);

      messageIds.forEach((id) => {
        global = updateChatMessage(global, chatId, id, { isPinned: shouldBePinned });
      });

      const messageIdsByThreadId = groupMessageIdsByThreadId(global, chatId, messageIds, false);

      Object.entries(messageIdsByThreadId).forEach(([threadId, ids]) => {
        const pinnedIds = selectPinnedIds(global, chatId, threadId) || [];
        const newPinnedIds = isPinned
          ? unique(pinnedIds.concat(ids)).sort((a, b) => b - a)
          : pinnedIds.filter((id) => !ids.includes(id));
        global = replaceThreadLocalStateParam(global, chatId, threadId, 'pinnedIds', newPinnedIds);
      });
      setGlobal(global);

      break;
    }

    case 'updateThreadInfo': {
      const {
        threadInfo,
      } = update;

      global = updateThreadInfo(global, threadInfo);
      setGlobal(global);

      break;
    }

    case 'updateThreadReadState': {
      const {
        chatId, threadId, readState,
      } = update;

      global = updateThreadReadState(global, chatId, threadId, readState);

      // Reading up to a message also reads every mention up to it on the backend, but the read it
      // broadcasts to our other devices carries no mention count — so they kept the "@" for good
      const unreadMentionsCount = selectThreadReadState(global, chatId, threadId)?.unreadMentionsCount;
      if (readState.lastReadInboxMessageId && !('unreadMentionsCount' in readState) && unreadMentionsCount) {
        if (readState.unreadCount === 0) {
          global = updateThreadReadState(global, chatId, threadId, {
            unreadMentionsCount: 0,
            unreadMentions: undefined,
          });
        } else {
          actions.loadUnreadMentions({ chatId, threadId });
        }
      }

      setGlobal(global);

      break;
    }

    case 'resetMessages': {
      const { id: chatId } = update;
      const messagesById = selectChatMessages(global, chatId);

      if (messagesById && !isUserId(chatId)) {
        const tabId = getCurrentTabId();
        global = deleteChatMessages(global, chatId, Object.keys(messagesById).map(Number));
        setGlobal(global);
        actions.loadFullChat({ chatId, force: true });
        actions.loadViewportMessages({ chatId, threadId: MAIN_THREAD_ID, tabId });
      }

      break;
    }

    case 'deleteMessages': {
      const { ids, chatId } = update;

      deleteMessages(global, chatId, ids, actions);
      break;
    }

    case 'deleteScheduledMessages': {
      const { ids, newIds, chatId } = update;

      const hadVideoProcessing = ids?.some((id) => (
        selectScheduledMessage(global, chatId, id)?.isVideoProcessingPending
      ));
      const processedVideoId = newIds?.find((id) => {
        const message = selectChatMessage(global, chatId, id);
        return message?.content.video;
      });

      if (hadVideoProcessing && processedVideoId) {
        Object.values(global.byTabId).forEach(({ id: tabId }) => {
          actions.showNotification({
            message: {
              key: 'VideoConversionDone',
            },
            actionText: {
              key: 'VideoConversionView',
            },
            action: {
              action: 'focusMessage',
              payload: {
                chatId,
                messageId: processedVideoId,
                tabId,
              },
            },
            tabId,
          });
        });
      }

      deleteScheduledMessages(chatId, ids, actions, global);
      break;
    }

    case 'deleteHistory': {
      const { chatId } = update;
      const chatMessages = global.messages.byChatId[chatId];
      if (chatId === SERVICE_NOTIFICATIONS_USER_ID) {
        global = {
          ...global,
          serviceNotifications: global.serviceNotifications.map((notification) => ({
            ...notification,
            isDeleted: true,
          })),
        };
        setGlobal(global);
      }

      if (chatMessages) {
        const ids = Object.keys(chatMessages.byId).map(Number);
        global = getGlobal();
        deleteMessages(global, chatId, ids, actions);
      } else {
        actions.requestChatUpdate({ chatId });
      }

      global = getGlobal();
      global = removeChatFromChatLists(global, chatId);
      setGlobal(global);

      break;
    }

    case 'deleteSavedHistory': {
      const { chatId } = update;
      const currentUserId = global.currentUserId!;
      global = removeChatFromChatLists(global, chatId, 'saved');
      setGlobal(global);

      global = getGlobal();
      deleteThread(global, currentUserId, chatId, actions);

      break;
    }

    case 'deleteParticipantHistory': {
      const { chatId, peerId } = update;

      global = getGlobal();
      deleteParticipantHistory(global, chatId, peerId, actions);

      break;
    }

    case 'updateCommonBoxMessages': {
      const { ids, messageUpdate } = update;

      ids.forEach((id) => {
        const chatId = selectCommonBoxChatId(global, id);
        if (chatId) {
          global = updateChatMessage(global, chatId, id, messageUpdate);
        }
      });

      setGlobal(global);

      break;
    }

    case 'updateChannelMessages': {
      const { channelId, ids, messageUpdate } = update;

      ids.forEach((id) => {
        global = updateChatMessage(global, channelId, id, messageUpdate);
      });

      setGlobal(global);

      break;
    }

    case 'updateMessagePoll': {
      const { pollId, pollUpdate } = update;

      global = updatePoll(global, pollId, pollUpdate);

      setGlobal(global);
      break;
    }

    case 'updateMessagePollVote': {
      const { pollId, peerId, options } = update;
      global = updatePollVote(global, pollId, peerId, options);
      setGlobal(global);

      break;
    }

    case 'updateMessagePollUnread': {
      const { chatId, messageId, threadId } = update;
      const readState = selectThreadReadState(global, chatId, threadId);

      if (!readState?.unreadPollVotes) {
        actions.loadUnreadPollVotes({ chatId, threadId });
        break;
      }

      if (readState.unreadPollVotes.includes(messageId)) break;

      // We can't calculate threads without local messages, so reload instead.
      if (!selectChatMessage(global, chatId, messageId)) {
        actions.loadUnreadPollVotes({ chatId, threadId });
        break;
      }

      global = addUnreadPollVotes({ global, chatId, ids: [messageId] });
      setGlobal(global);

      break;
    }

    case 'updateServiceNotification': {
      const { message } = update;

      if (selectIsServiceChatReady(global)) {
        actions.createServiceNotification({ message });
      }

      break;
    }

    case 'updateMessageReactions': {
      const {
        chatId, id, threadId, reactions, requestId,
      } = update;

      global = updateReactions(global, actions, {
        chatId, id, threadId, reactions, requestId,
      });
      setGlobal(global);
      break;
    }

    case 'updateMessageExtendedMedia': {
      const {
        chatId, id, extendedMedia, isBought,
      } = update;
      const message = selectChatMessage(global, chatId, id);
      const chat = selectChat(global, update.chatId);

      if (!chat || !message) return;

      if (message.content.invoice) {
        const media = extendedMedia[0];
        if ('mediaType' in media && media.mediaType === 'extendedMediaPreview') {
          if (!message.content.invoice) return;
          global = updateChatMessage(global, chatId, id, {
            content: {
              ...message.content,
              invoice: {
                ...message.content.invoice,
                extendedMedia: media,
              },
            },
          });
          setGlobal(global);
        } else {
          const content = media as MediaContent;
          global = updateChatMessage(global, chatId, id, {
            content: {
              ...content,
            },
          });
          setGlobal(global);
        }
      }

      if (message.content.paidMedia) {
        const paidMediaUpdate = isBought ? { isBought, extendedMedia }
          : { extendedMedia: extendedMedia as ApiMediaExtendedPreview[], isBought: undefined };

        global = updateChatMessage(global, chatId, id, {
          content: {
            ...message.content,
            paidMedia: {
              ...message.content.paidMedia,
              ...paidMediaUpdate,
            },
          },
        });
        setGlobal(global);
      }

      break;
    }

    case 'updateTranscribedAudio': {
      const { transcriptionId, text, isPending } = update;

      global = {
        ...global,
        transcriptions: {
          ...global.transcriptions,
          [transcriptionId]: {
            ...(global.transcriptions[transcriptionId] || {}),
            transcriptionId,
            text,
            isPending,
          },
        },
      };
      setGlobal(global);
      break;
    }

    case 'updateMessageSendFailed': {
      const { chatId, localId, error } = update;

      if (error.match(/CHAT_SEND_.+?FORBIDDEN/)) {
        Object.values(global.byTabId).forEach(({ id: tabId }) => {
          actions.showAllowedMessageTypesNotification({ chatId, tabId });
        });
      }

      global = updateChatMessage(global, chatId, localId, {
        sendingState: 'messageSendingStateFailed',
        isResendable: update.canResend,
      });
      setGlobal(global);
      break;
    }

    case 'updateScheduledMessageSendFailed': {
      const { chatId, localId, error } = update;

      if (error.match(/CHAT_SEND_.+?FORBIDDEN/)) {
        Object.values(global.byTabId).forEach(({ id: tabId }) => {
          actions.showAllowedMessageTypesNotification({ chatId, tabId });
        });
      }

      global = updateScheduledMessage(global, chatId, localId, {
        sendingState: 'messageSendingStateFailed',
        isResendable: update.canResend,
      });
      setGlobal(global);
      break;
    }

    case 'updateMessageTranslations': {
      const {
        chatId, messageIds, toLanguageCode, translations, tone,
      } = update;

      global = updateMessageTranslations(global, chatId, messageIds, toLanguageCode, translations, tone);

      setGlobal(global);
      break;
    }

    case 'failedMessageTranslations': {
      const { chatId, messageIds, toLanguageCode, tone } = update;

      global = updateMessageTranslations(global, chatId, messageIds, toLanguageCode, [], tone);

      setGlobal(global);
      break;
    }

    case 'updateChatTypingDraft': {
      const { id, chatId, threadId = MAIN_THREAD_ID, text } = update;
      const thread = selectThread(global, chatId, threadId);
      if (!thread) return undefined;

      let typingDraftStore = selectThreadLocalStateParam(global, chatId, threadId, 'typingDraftIdByRandomId');
      const messageId = typingDraftStore?.[id];

      const isUpdatingDraft = Boolean(messageId);
      const updatingMessage = isUpdatingDraft ? selectChatMessage(global, chatId, messageId) : undefined;

      const rescheduleDraftRemoval = () => {
        // Clear typing draft after timeout
        setTimeout(() => {
          global = getGlobal();
          const currentTypingDraftStore = selectThreadLocalStateParam(
            global, chatId, threadId, 'typingDraftIdByRandomId',
          );
          if (currentTypingDraftStore?.[id]) {
            const currentMessageId = currentTypingDraftStore[id];
            const currentMessage = selectChatMessage(global, chatId, currentMessageId);
            // Already deleted or replaced with a new message
            if (!currentMessage || getServerTime() - currentMessage.editDate! < global.appConfig.typingDraftTtl) return;

            const newTypingDraftIds = omit(currentTypingDraftStore, [id]);
            global = replaceThreadLocalStateParam(
              global, chatId, threadId, 'typingDraftIdByRandomId', newTypingDraftIds,
            );
            global = deleteChatMessages(global, chatId, [currentMessageId]);
            setGlobal(global);
          }
        }, global.appConfig.typingDraftTtl * 1000);
      };

      if (isUpdatingDraft && updatingMessage) {
        global = updateChatMessage(global, chatId, messageId, {
          content: {
            text,
          },
          editDate: getServerTime(),
        });
        rescheduleDraftRemoval();
        return global;
      }

      const newMessage = createApiMessageFromTypingDraft({
        chatId,
        threadId,
        text,
      });

      actions.apiUpdate({
        '@type': 'newMessage',
        chatId,
        id: newMessage.id,
        message: newMessage,
      });

      typingDraftStore = {
        ...typingDraftStore,
        [id]: newMessage.id,
      };
      global = replaceThreadLocalStateParam(global, chatId, threadId, 'typingDraftIdByRandomId', typingDraftStore);

      rescheduleDraftRemoval();

      return global;
    }
  }
});

function updateReactions<T extends GlobalState>(
  global: T,
  actions: RequiredGlobalActions,
  {
    chatId, id, threadId, reactions, requestId,
  }: {
    chatId: string;
    id: number;
    threadId?: ThreadId;
    reactions: ApiReactions;
    requestId?: number;
  },
): T {
  const chat = selectChat(global, chatId);
  const message = selectChatMessage(global, chatId, id);

  if (!chat || !message) {
    // Simplified logic to update counter only
    const hasUnread = checkIfHasUnreadReactions(global, reactions);
    if (hasUnread) {
      global = addUnreadReactions({ global, chatId, ids: [id] });
    } else {
      // Reload unread reactions to update counter
      actions.loadUnreadReactions({ chatId, threadId });
    }
    return global;
  }

  // This is the echo of our own `sendReaction` call, but a newer toggle on this message has already
  // superseded it (its response can resolve out of order) — drop it so it doesn't clobber fresher state.
  if (requestId !== undefined && !isLatestReactionRequest(getMessageKey(message), requestId)) {
    return global;
  }

  const currentReactions = message?.reactions;

  // `updateMessageReactions` happens with an interval, so we try to avoid redundant global state updates
  if (currentReactions && areDeepEqual(reactions, currentReactions)) {
    return global;
  }

  const localPaidReaction = currentReactions?.results.find((r) => r.localAmount);
  // Save local count on update, but reset if we sent reaction
  if (localPaidReaction?.localAmount) {
    const { localIsPrivate: isPrivate, localAmount, localPeerId } = localPaidReaction;
    reactions.results = addPaidReaction(reactions.results, localAmount, isPrivate, localPeerId);
  }

  global = updateChatMessage(global, chatId, id, { reactions });

  if (!message.isOutgoing) {
    return global;
  }

  const recentReaction = reactions.recentReactions?.[0];
  const reactionEffectsEnabled = selectPerformanceSettingsValue(global, 'reactionEffects');
  const messageKey = getMessageKey(message);
  if (
    reactionEffectsEnabled && recentReaction
    && checkIfReactionIsUnreadByOther(global, recentReaction)
    && !checkIfReactionEffectIsStale(recentReaction.addedDate)
    && !checkIfReactionEffectPlayed(messageKey, recentReaction)
  ) {
    markReactionEffectPlayed(messageKey, recentReaction);
    // Start reaction only in master tab
    actions.startActiveReaction({
      containerId: messageKey, reaction: recentReaction.reaction, tabId: getCurrentTabId(),
    });
  }

  const hasUnreadReactionsForMessageInChat = message.reactions && checkIfHasUnreadReactions(global, message.reactions);
  const hasUnreadReactionsInNewReactions = checkIfHasUnreadReactions(global, reactions);

  // Only notify about added reactions, not removed ones
  if (hasUnreadReactionsInNewReactions && !hasUnreadReactionsForMessageInChat) {
    global = addUnreadReactions({ global, chatId, ids: [id] });

    const newMessage = selectChatMessage(global, chatId, id);

    if (!chat || !newMessage) return global;

    onTickEnd(() => {
      notifyAboutMessage({
        chat,
        message: newMessage,
        isReaction: true,
      });
    });
  }

  if (!hasUnreadReactionsInNewReactions && hasUnreadReactionsForMessageInChat) {
    global = removeUnreadReactions({ global, chatId, ids: [id] });
  }

  return global;
}

export function updateWithLocalMedia(
  global: RequiredGlobalState,
  chatId: string,
  id: number,
  isNew: boolean,
  messageUpdate: Partial<ApiMessage>,
  isScheduled = false,
) {
  const currentMessage = isScheduled
    ? selectScheduledMessage(global, chatId, id)
    : selectChatMessage(global, chatId, id);

  if (!currentMessage && !isNew) return global;

  // Preserve locally uploaded media, but only while the update still carries the same media —
  // an edit that swaps the file comes back with a different media id, and grafting the previous
  // `blobUrl` onto it kept the bubble showing the old video/photo until a reload.
  if (currentMessage && messageUpdate.content && !isLocalMessageId(id)) {
    const {
      photo, video, sticker, document,
    } = getMessageContent(currentMessage);

    if (photo && messageUpdate.content.photo && isSameMedia(photo, messageUpdate.content.photo)) {
      messageUpdate.content.photo.blobUrl ??= photo.blobUrl;
      messageUpdate.content.photo.thumbnail ??= photo.thumbnail;
    } else if (video && messageUpdate.content.video && isSameMedia(video, messageUpdate.content.video)) {
      messageUpdate.content.video.blobUrl ??= video.blobUrl;
    } else if (sticker && messageUpdate.content.sticker && isSameMedia(sticker, messageUpdate.content.sticker)) {
      messageUpdate.content.sticker.isPreloadedGlobally ??= sticker.isPreloadedGlobally;
    } else if (document && messageUpdate.content.document && isSameMedia(document, messageUpdate.content.document)) {
      messageUpdate.content.document.previewBlobUrl ??= document.previewBlobUrl;
    }
  }

  const newMessage = currentMessage ? { ...currentMessage, ...messageUpdate } : messageUpdate;

  return isScheduled
    ? updateScheduledMessage(global, chatId, id, newMessage)
    : updateChatMessage(global, chatId, id, newMessage);
}

// A media still being uploaded carries the temp id, so the first server echo (which brings the real
// one) counts as the same media — that is the case the local blob is kept for
function isSameMedia(current: { id?: string }, next: { id?: string }) {
  return current.id === next.id || current.id === LOCAL_MEDIA_UPLOADING_TEMP_ID;
}

function updateListedAndViewportIds<T extends GlobalState>(
  global: T, message: ApiMessage,
) {
  const { id, chatId } = message;

  const savedDialogId = selectSavedDialogIdFromMessage(global, message);
  const threadId = savedDialogId || selectThreadIdFromMessage(global, message);
  const threadInfo = selectThreadInfo(global, chatId, threadId);

  const mainThreadReadState = selectThreadReadState(global, chatId, MAIN_THREAD_ID);
  const isUnreadChatNotLoaded = mainThreadReadState?.unreadCount && !selectListedIds(global, chatId, MAIN_THREAD_ID);

  if (threadId) {
    global = updateListedIds(global, chatId, threadId, [id]);

    Object.values(global.byTabId).forEach(({ id: tabId }) => {
      if (selectIsViewportNewest(global, chatId, threadId, tabId)) {
        global = addViewportIdPreservingFirstUnread(global, chatId, threadId, id, tabId);
      }
    });

    if (threadInfo) {
      global = updateThreadInfoLastMessageId(global, chatId, threadId, message.id);

      if (!isMessageLocal(message) && !isActionMessage(message)) {
        const newCount = (threadInfo.messagesCount || 0) + 1;
        global = updateThreadInfoMessagesCount(global, chatId, threadId, newCount);
      }
    }
  }

  if (isUnreadChatNotLoaded) {
    return global;
  }

  global = updateListedIds(global, chatId, MAIN_THREAD_ID, [id]);

  Object.values(global.byTabId).forEach(({ id: tabId }) => {
    if (selectIsViewportNewest(global, chatId, MAIN_THREAD_ID, tabId)) {
      global = addViewportIdPreservingFirstUnread(global, chatId, MAIN_THREAD_ID, id, tabId);
    }
  });

  return global;
}

// Adds a newly arrived message to the viewport, unless doing so would push the first unread
// message out of it. `addViewportId` trims the viewport once it grows past
// `MESSAGE_LIST_VIEWPORT_LIMIT`, so on a busy chat the first unread message is often already
// outside the viewport by the time a new message arrives — in that case there's nothing left to
// preserve, and blocking the update here would permanently stop new messages from rendering until
// the viewport is reloaded (e.g. via the "jump to bottom" button).
function addViewportIdPreservingFirstUnread<T extends GlobalState>(
  global: T, chatId: string, threadId: ThreadId, id: number, tabId: number,
) {
  const firstUnreadId = selectFirstUnreadId(global, chatId, threadId);
  const viewportIdsBefore = selectViewportIds(global, chatId, threadId, tabId);
  const wasFirstUnreadInViewport = !firstUnreadId || Boolean(viewportIdsBefore?.includes(firstUnreadId));

  const candidateGlobal = addViewportId(global, chatId, threadId, id, tabId);

  if (!wasFirstUnreadInViewport) {
    return candidateGlobal;
  }

  const newViewportIds = selectViewportIds(candidateGlobal, chatId, threadId, tabId);
  return !firstUnreadId || newViewportIds!.includes(firstUnreadId) ? candidateGlobal : global;
}

function updateChatLastMessage<T extends GlobalState>(
  global: T,
  chatId: string,
  message: ApiMessage,
  force = false,
) {
  // A scheduled post has not been published yet, so it must never surface as the chat's last message
  // — otherwise subscribers read its text in the chat-list preview before it ever goes out. The
  // backend is expected to flag it (`isScheduled`); this guard is what makes the client honour that.
  if (message.isScheduled) {
    return global;
  }

  const currentLastMessageId = selectChatLastMessageId(global, chatId);

  const threadId = selectThreadIdFromMessage(global, message);
  global = updateThreadInfoLastMessageId(global, chatId, threadId, message.id);

  const savedDialogId = selectSavedDialogIdFromMessage(global, message);
  if (savedDialogId) {
    global = updateChatLastMessageId(global, savedDialogId, message.id, 'saved');
  }

  if (currentLastMessageId && !force) {
    const isSameOrNewer = (
      currentLastMessageId === message.id || currentLastMessageId === message.previousLocalId
    ) || message.id > currentLastMessageId;

    // A dangling `currentLastMessageId` — its message was removed from the store (e.g. after clearing a
    // bot chat's history) — must not block the update. Otherwise `lastMessageId` keeps pointing at a
    // deleted message, `selectChatLastMessage` resolves to `undefined`, and a just-started bot still
    // looks "not started", hiding the composer even after pressing Start.
    const currentLastMessageExists = Boolean(selectChatMessage(global, chatId, currentLastMessageId));

    if (currentLastMessageExists && !isSameOrNewer) {
      return global;
    }
  }

  global = updateChatLastMessageId(global, chatId, message.id);

  return global;
}

function findLastMessage<T extends GlobalState>(global: T, chatId: string, threadId: ThreadId = MAIN_THREAD_ID) {
  const byId = selectChatMessages(global, chatId);
  const listedIds = selectListedIds(global, chatId, threadId);

  if (!byId || !listedIds) {
    return undefined;
  }

  let i = listedIds.length;
  while (i--) {
    const message = byId[listedIds[i]];
    if (message && !message.isDeleting) {
      return message;
    }
  }

  return undefined;
}

export function deleteParticipantHistory<T extends GlobalState>(
  global: T,
  chatId: string,
  peerId: string,
  actions: RequiredGlobalActions,
) {
  const byId = selectChatMessages(global, chatId);

  const messageIds = Object.values(byId).filter((message) => {
    return message.senderId === peerId;
  }).map((message) => message.id);

  if (!messageIds.length) {
    return;
  }

  deleteMessages(global, chatId, messageIds, actions);
}

export function deleteThread<T extends GlobalState>(
  global: T,
  chatId: string,
  threadId: ThreadId,
  actions: RequiredGlobalActions,
) {
  const byId = selectChatMessages(global, chatId);
  if (!byId) {
    return;
  }

  const messageIds = Object.values(byId).filter((message) => {
    const messageThreadId = selectThreadIdFromMessage(global, message);
    return messageThreadId === threadId;
  }).map((message) => message.id);

  if (!messageIds.length) {
    return;
  }

  deleteMessages(global, chatId, messageIds, actions);
}

export function deleteMessages<T extends GlobalState>(
  global: T, chatId: string | undefined, ids: number[], actions: RequiredGlobalActions,
) {
  // Channel update

  if (chatId) {
    const chat = selectChat(global, chatId);
    if (!chat) return;

    closeRemovedMessageNotifications(chatId, ids);

    const threadIdsToUpdate = new Set<ThreadId>();
    threadIdsToUpdate.add(MAIN_THREAD_ID);
    let hasDeletedTrainingRecording = false;

    ids.forEach((id) => {
      global = updateChatMessage(global, chatId, id, {
        isDeleting: true,
      });

      if (selectTopic(global, chatId, id)) {
        global = deleteTopic(global, chatId, id);
      }

      const message = selectChatMessage(global, chatId, id);
      if (!message) {
        return;
      }

      if (message.content.action?.type === 'chatEditPhoto' && message.content.action.photo) {
        global = deletePeerPhoto(global, chatId, message.content.action.photo.id, true);
      }

      hasDeletedTrainingRecording ||= isTrainingRecordingMessage(message);

      const threadId = selectThreadIdFromMessage(global, message);
      if (threadId) {
        threadIdsToUpdate.add(threadId);
      }
    });

    actions.requestChatUpdate({ chatId });
    if (hasDeletedTrainingRecording) {
      refreshTrainingRecordingCounters(global, [chatId], actions);
    }

    const idsSet = new Set(ids);

    threadIdsToUpdate.forEach((threadId) => {
      if (chat.isForum && threadId !== MAIN_THREAD_ID) {
        // Refresh unread count
        actions.loadTopicById({ chatId, topicId: Number(threadId) });
      }

      const threadInfo = selectThreadInfo(global, chatId, threadId);
      if (!threadInfo?.lastMessageId || !idsSet.has(threadInfo.lastMessageId)) return;

      const newLastMessage = findLastMessage(global, chatId, threadId);

      if (!newLastMessage) {
        return;
      }

      if (threadId === MAIN_THREAD_ID) {
        global = updateChatLastMessage(global, chatId, newLastMessage, true);
      }

      global = updateThreadInfoLastMessageId(global, chatId, threadId, newLastMessage.id);
    });

    setGlobal(global);

    const isAnimatingAsSnap = selectCanAnimateSnapEffect(global);

    setTimeout(() => {
      global = getGlobal();
      // Prevent local deletion of sent messages in case of desync
      const stillDeletedIds = ids.filter((id) => selectChatMessage(global, chatId, id)?.isDeleting);
      global = deleteChatMessages(global, chatId, stillDeletedIds);
      setGlobal(global);
    }, isAnimatingAsSnap ? SNAP_ANIMATION_DELAY : ANIMATION_DELAY);

    return;
  }

  // Common box update

  const chatIdsToUpdate: string[] = [];
  const chatIdsWithDeletedTrainingRecordings: string[] = [];

  ids.forEach((id) => {
    const commonBoxChatId = selectCommonBoxChatId(global, id);
    if (commonBoxChatId) {
      chatIdsToUpdate.push(commonBoxChatId);
      closeRemovedMessageNotifications(commonBoxChatId, [id]);

      global = updateChatMessage(global, commonBoxChatId, id, {
        isDeleting: true,
      });

      const newLastMessage = findLastMessage(global, commonBoxChatId);
      if (newLastMessage) {
        global = updateChatLastMessage(global, commonBoxChatId, newLastMessage, true);
      }

      const message = selectChatMessage(global, commonBoxChatId, id);
      if (selectIsChatWithSelf(global, commonBoxChatId) && message) {
        const threadId = selectThreadIdFromMessage(global, message);
        if (getIsSavedDialog(commonBoxChatId, threadId, global.currentUserId)) {
          const newLastSavedDialogMessage = findLastMessage(global, commonBoxChatId, threadId);
          actions.requestSavedDialogUpdate({ chatId: String(threadId) });
          if (newLastSavedDialogMessage) {
            global = updateChatLastMessageId(global, commonBoxChatId, newLastSavedDialogMessage.id, 'saved');
          }
        }
      }

      if (message?.content.action?.type === 'chatEditPhoto' && message.content.action.photo) {
        global = deletePeerPhoto(global, commonBoxChatId, message.content.action.photo.id, true);
      }

      if (message && isTrainingRecordingMessage(message)) {
        chatIdsWithDeletedTrainingRecordings.push(commonBoxChatId);
      }

      const isAnimatingAsSnap = selectCanAnimateSnapEffect(global);

      setTimeout(() => {
        global = getGlobal();
        global = deleteChatMessages(global, commonBoxChatId, [id]);
        setGlobal(global);
      }, isAnimatingAsSnap ? SNAP_ANIMATION_DELAY : ANIMATION_DELAY);
    }
  });

  setGlobal(global);

  unique(chatIdsToUpdate).forEach((id) => {
    actions.requestChatUpdate({ chatId: id });
  });

  if (chatIdsWithDeletedTrainingRecordings.length) {
    refreshTrainingRecordingCounters(global, chatIdsWithDeletedTrainingRecordings, actions);
  }
}

// The training-recordings tab is shown by its count, so a deleted recording must update it in any open chat info
function refreshTrainingRecordingCounters<T extends GlobalState>(
  global: T, chatIds: string[], actions: RequiredGlobalActions,
) {
  Object.values(global.byTabId).forEach(({ id: tabId }) => {
    const currentChatId = selectCurrentMessageList(global, tabId)?.chatId;
    if (!currentChatId || !chatIds.includes(currentChatId)) return;
    if (!selectCurrentSharedMediaSearch(global, tabId)?.counters) return;

    actions.loadSharedMediaCounters({ tabId });
  });
}

function deleteScheduledMessages<T extends GlobalState>(
  chatId: string, ids: number[], actions: RequiredGlobalActions, global: T,
) {
  ids.forEach((id) => {
    global = updateScheduledMessage(global, chatId, id, {
      isDeleting: true,
    });
  });

  setGlobal(global);

  const isAnimatingAsSnap = selectCanAnimateSnapEffect(global);

  setTimeout(() => {
    global = getGlobal();
    global = deleteChatScheduledMessages(global, chatId, ids);
    setGlobal(global);
  }, isAnimatingAsSnap ? SNAP_ANIMATION_DELAY : ANIMATION_DELAY);
}

// The message a draft is replying to was taken back: the reply can no longer be sent, so drop it from
// the composer and keep the typed text. Only acts while the draft still points at it, so a repeated
// update is a no-op.
function cancelRepliesToRecalledMessage<T extends GlobalState>(
  global: T, actions: RequiredGlobalActions, chatId: string, messageId: number,
) {
  Object.keys(global.byTabId).forEach((key) => {
    const tabId = Number(key);
    const currentMessageList = selectCurrentMessageList(global, tabId);
    if (currentMessageList?.chatId !== chatId) return;

    const draft = selectDraft(global, chatId, currentMessageList.threadId);
    if (draft?.replyInfo?.replyToMsgId !== messageId) return;

    actions.resetDraftReplyInfo({ tabId });
    actions.showNotification({ message: { key: 'MessageRecalled' }, tabId });
  });
}
